import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {onlyItem} from '../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {createSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useAssignBulkActionPermission} from '../../../../../../srcCommon/permissions/useBulkActionPermission';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {findConversationNamespaces} from '../../../../../helpers/models/conversationHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../helpers/models/tagHelpers';
import {PermissionDeniedReasonsEnum} from '../../../../../helpers/permissionHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {getComments} from '../../../../../store/data/dataSelectors';
import {getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {PermissionPopoverCoordinator} from '../../../../common/popovers/permissionPopoverCoordinator';
import {buildHighlighterProps} from '../../componentHighlighter';
import {LazyAssignDropdownContainer} from './assign/lazyAssignDropdownContainer';

/*
 * Props.
 */

interface ConversationTopbarAssignProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  assigneeAll?: TeammateModel;
  inboxesParameters: InboxesParameters;
  isBulkActionRequired: boolean;
}

/*
 * Style.
 */

const StyledAssigneeDiv = styled.div`
  display: flex;
  align-items: center;
  margin: -3px -5px -4px -10px;
`;

const StyledAssignDiv = styled(StyledAssigneeDiv)`
  margin: 0;
  margin-right: -5px;
`;

const StyledNameDiv = styled.div`
  margin: -1px 3px 0 5px;
  max-width: 120px;
  ${ellipsis()};
`;

const StyledAssignTextDiv = styled.div`
  max-width: 120px;
  ${ellipsis()};
  margin-right: 5px;
  margin-left: 1px;
`;

/*
 * Component.
 */

export const ConversationTopbarAssign: FC<ConversationTopbarAssignProps> = (props) => {
  const {conversations, assigneeAll, isBulkActionRequired, inboxesParameters} = props;
  const bulkActionPermission = useAssignBulkActionPermission(isBulkActionRequired, inboxesParameters);

  // Sandbox highlighting code - verifying that this will be useful to customers
  const shouldHighlightAssign = useShouldHighlightAssign(conversations);

  return (
    <PermissionPopoverCoordinator
      namespaces={findConversationNamespaces(conversations)}
      permission={PermissionsEnum.CONVERSATION_ASSIGN}
      renderButton={(isOpen) => {
        const shouldHighlightButton = !isOpen && shouldHighlightAssign;
        const assignButtonHighlighterProps = buildHighlighterProps(
          'assign-button',
          'button-highlighter',
          shouldHighlightButton,
        );

        return renderButton(isOpen, assigneeAll, assignButtonHighlighterProps);
      }}
      renderDisabledButton={(reason) => renderNoAccessButton(reason, assigneeAll)}
      renderModal={(modalProps) => renderDropdown(modalProps, props, shouldHighlightAssign)}
      keyboardAction={KeyboardActionsEnum.ASSIGN}
      bulkActionPermission={bulkActionPermission}
    />
  );
};

function useShouldHighlightAssign(conversations: ReadonlyArray<ConversationBaseModel>) {
  const userId = useAppSelector(getUserId);

  const onlyConversation = onlyItem(conversations);
  const lastCommentUid =
    onlyConversation && 'lastComment' in onlyConversation
      ? (onlyConversation as ConversationModel).lastComment?.uid
      : undefined;

  const getIsLastCommentGuidedDemoAssign = useMemo(
    () =>
      createSelector([getComments], (comments) => {
        const lastComment = lastCommentUid ? comments[lastCommentUid] : undefined;
        return lastComment?.text.includes(':point_right: **Assign** the conversation to yourself.');
      }),
    [lastCommentUid],
  );

  const isLastCommentGuidedDemoAssign = useAppSelector(getIsLastCommentGuidedDemoAssign);

  return Boolean(
    onlyConversation &&
      'isGuidedDemo' in onlyConversation &&
      onlyConversation?.isGuidedDemo &&
      isLastCommentGuidedDemoAssign &&
      onlyConversation.assigneeId !== userId,
  );
}

/*
 * Button.
 */

function renderButton(isOpen: boolean, assigneeAll?: TeammateModel, highlighterProps?: {className: string}) {
  return (
    <Button {...highlighterProps} hasHover={isOpen} interactionId="conversation-topbar-assign">
      <TooltipCoordinator render={() => maybeRenderTooltip(assigneeAll)}>
        {renderButtonContent(assigneeAll)}
      </TooltipCoordinator>
    </Button>
  );
}

function renderNoAccessButton(reason: PermissionDeniedReasonsEnum, assigneeAll?: TeammateModel) {
  return (
    <TooltipCoordinator render={() => <PermissionDenied reason={reason} />}>
      <Button isDisabled>{renderButtonContent(assigneeAll)}</Button>
    </TooltipCoordinator>
  );
}

function maybeRenderTooltip(assigneeAll?: TeammateModel) {
  if (!assigneeAll) {
    return null;
  }

  return <Trans id="conversation-topbar-assign-tooltip">Assignee</Trans>;
}

function renderButtonContent(assigneeAll?: TeammateModel) {
  // If we don't have an assignee, just render the button.
  if (!assigneeAll) {
    return (
      <StyledAssignDiv>
        <StyledAssignTextDiv>
          <Trans id="conversation-topbar-assign">Assign</Trans>
        </StyledAssignTextDiv>
      </StyledAssignDiv>
    );
  }

  // Otherwise, render the assignee.
  return (
    <StyledAssigneeDiv>
      <TeammateAvatar teammate={assigneeAll} size={24} />
      <StyledNameDiv>{buildTeammateDisplayName(assigneeAll)}</StyledNameDiv>
      <Icon name="chevronDownSmall" />
    </StyledAssigneeDiv>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(
  modalProps: PopoverTransferProps,
  props: ConversationTopbarAssignProps,
  shouldHighlightAssign: boolean,
) {
  return (
    <LazyAssignDropdownContainer
      {...modalProps}
      placement="bottom-end"
      cellsContext={props.cellsContext}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      inboxesParameters={props.inboxesParameters}
      interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.TOPBAR}
      shouldHighlightAssign={shouldHighlightAssign}
    />
  );
}
